// Staff and invitations (FR-42). A company_admin changes roles, removes staff,
// invites by email (a single-use, 72-hour link) and revokes pending
// invitations; a company_viewer only reads. The server refuses to remove or
// demote the last company_admin (409) and its message is shown as is.
import { Badge, Button, Group, Loader, NativeSelect, Paper, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { useMe } from "../../shared/session/me";
import { useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { companyKey, ConfirmDialog, MINE_KEY, ROLES, type StaffRole } from "./common";

type Member = Schemas["StaffMember"];
type Invitation = Schemas["Invitation"];

const INVITE_COLOUR: Record<Invitation["status"], string> = {
  pending: "yellow",
  accepted: "green",
  expired: "gray",
  revoked: "red",
};

export function StaffTab({ companyId, admin }: { companyId: string; admin: boolean }) {
  const { t } = useTranslation();
  const f = useFormat();
  const me = useMe();
  const queryClient = useQueryClient();
  const key = companyKey(companyId, "staff");
  const staff = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/companies/{company_id}/staff", { params: { path: { company_id: companyId } } })),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: MINE_KEY });
  };
  const roleLabel = (r: StaffRole) => t(`staff_role.${r}`);

  const setRole = async (m: Member, role: StaffRole) => {
    setError(null);
    setNotice(null);
    const { data, error } = await api.PATCH("/companies/{company_id}/staff/{account_id}", {
      params: { path: { company_id: companyId, account_id: m.account_id } },
      body: { role },
    });
    if (!data) {
      setError(errorMessage(error, t("error.generic")));
      return refresh(); // put the select back to the stored role
    }
    setNotice(t("staff.role_changed", { email: m.email_hint, role: roleLabel(data.role) }));
    refresh();
  };

  const remove = async () => {
    if (!removing) return;
    setRemoveError(null);
    setBusy(true);
    const { error, response } = await api.DELETE("/companies/{company_id}/staff/{account_id}", {
      params: { path: { company_id: companyId, account_id: removing.account_id } },
    });
    setBusy(false);
    if (!response.ok) return setRemoveError(errorMessage(error, t("error.generic")));
    setNotice(t("staff.removed", { email: removing.email_hint }));
    setRemoving(null);
    refresh();
  };

  const revokeInvitation = async (i: Invitation) => {
    setError(null);
    setNotice(null);
    const { error, response } = await api.DELETE("/companies/{company_id}/invitations/{invitation_id}", {
      params: { path: { company_id: companyId, invitation_id: i.id } },
    });
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    setNotice(t("staff.invitation_revoked", { email: i.email_hint }));
    refresh();
  };

  if (staff.isPending) return <Loader size="sm" aria-label={t("common.loading")} />;
  if (staff.isError) return <FormError message={errorMessage(staff.error, t("error.generic"))} />;
  const { items, invitations } = staff.data;

  return (
    <Stack gap="md">
      {notice && <Notice>{notice}</Notice>}
      <FormError message={error} />
      {!admin && (
        <Text size="sm" c="dimmed">
          {t("staff.viewer_note")}
        </Text>
      )}
      <Title order={2} size="h4">
        {t("staff.members")}
      </Title>
      <Table.ScrollContainer minWidth={520}>
        <Table verticalSpacing="sm" aria-label={t("staff.members")}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("staff.email")}</Table.Th>
              <Table.Th>{t("staff.role")}</Table.Th>
              <Table.Th>{t("staff.since")}</Table.Th>
              {admin && <Table.Th>{t("staff.actions")}</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((m) => {
              const you = m.account_id === me.data?.account_id;
              return (
                <Table.Tr key={m.account_id} data-testid="staff-row">
                  <Table.Td>
                    {m.email_hint}
                    {you && (
                      <Badge ml={6} size="xs" variant="outline">
                        {t("staff.you")}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {admin ? (
                      <NativeSelect
                        size="xs"
                        aria-label={t("staff.role_for", { email: m.email_hint })}
                        value={m.role}
                        onChange={(e) => void setRole(m, e.currentTarget.value as StaffRole)}
                        data={ROLES.map((r) => ({ value: r, label: roleLabel(r) }))}
                      />
                    ) : (
                      roleLabel(m.role)
                    )}
                  </Table.Td>
                  <Table.Td>{f.date(m.created_at)}</Table.Td>
                  {admin && (
                    <Table.Td>
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          setRemoveError(null);
                          setRemoving(m);
                        }}
                        aria-label={t("staff.remove_for", { email: m.email_hint })}
                      >
                        {t("staff.remove")}
                      </Button>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {admin && (
        <InviteForm
          companyId={companyId}
          onInvited={(i) => {
            setError(null);
            setNotice(t("staff.invited", { email: i.email_hint }));
            refresh();
          }}
        />
      )}

      <Title order={2} size="h4">
        {t("staff.invitations")}
      </Title>
      {invitations.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid="invitations-empty">
          {t("staff.no_invitations")}
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={520}>
          <Table verticalSpacing="sm" aria-label={t("staff.invitations")}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("staff.email")}</Table.Th>
                <Table.Th>{t("staff.role")}</Table.Th>
                <Table.Th>{t("staff.invitation_status")}</Table.Th>
                <Table.Th>{t("staff.expires")}</Table.Th>
                {admin && <Table.Th>{t("staff.actions")}</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {invitations.map((i) => (
                <Table.Tr key={i.id} data-testid="invitation-row">
                  <Table.Td>{i.email_hint}</Table.Td>
                  <Table.Td>{roleLabel(i.role)}</Table.Td>
                  <Table.Td>
                    <Badge color={INVITE_COLOUR[i.status]} variant="light">
                      {t(`invitation_status.${i.status}`)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{f.dateTime(i.expires_at)}</Table.Td>
                  {admin && (
                    <Table.Td>
                      {i.status === "pending" && (
                        <Button
                          size="xs"
                          variant="subtle"
                          color="red"
                          onClick={() => void revokeInvitation(i)}
                          aria-label={t("staff.revoke_invitation_for", { email: i.email_hint })}
                        >
                          {t("staff.revoke_invitation")}
                        </Button>
                      )}
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <ConfirmDialog
        opened={removing !== null}
        title={t("staff.remove_title")}
        confirmLabel={t("staff.remove")}
        busy={busy}
        error={removeError}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      >
        {t("staff.remove_body", { email: removing?.email_hint ?? "" })}
      </ConfirmDialog>
    </Stack>
  );
}

function InviteForm({ companyId, onInvited }: { companyId: string; onInvited: (i: Invitation) => void }) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StaffRole>("company_viewer");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const { data, error } = await api.POST("/companies/{company_id}/invitations", {
      params: { path: { company_id: companyId } },
      body: { email: email.trim(), role, locale: i18n.language },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setEmail("");
    onInvited(data);
  };

  return (
    <Paper withBorder p="md" radius="md">
      <form onSubmit={submit} data-testid="invite-form">
        <Stack gap="sm">
          <Title order={2} size="h4">
            {t("staff.invite")}
          </Title>
          <Text size="sm" c="dimmed">
            {t("staff.invite_intro")}
          </Text>
          <Group align="flex-end" gap="sm">
            <TextInput
              type="email"
              label={t("staff.invite_email")}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
              style={{ flex: "1 1 220px" }}
            />
            <NativeSelect
              label={t("staff.invite_role")}
              value={role}
              onChange={(e) => setRole(e.currentTarget.value as StaffRole)}
              data={ROLES.map((r) => ({ value: r, label: t(`staff_role.${r}`) }))}
            />
          </Group>
          <FormError message={error} />
          <Button type="submit" loading={busy} w="fit-content">
            {t("staff.invite_submit")}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}
